import { Box, Chip, Link as MuLink } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { compressClassYearArray } from '../../../../../utils/compressClassYearArray';
import { getCardLinkProps } from '../../../../../utils/searchUtils';
import useRouter from '../../../../../utils/useRouter';
import AiMainTopic from '../AiMainTopic';
import {
	AddMenuButton,
	getItemLink,
	Metadata,
	type MuiCardBaseProps,
	mapContentTypeName,
} from '../MuiCardBase';
import CardCover from '../MuiCardBaseCover';
import CardContainer from './MuiCardBaseShortContainer';
import CardContentWrapper from './MuiCardBaseShortContentWrapper';
import CardDescription from './MuiCardBaseShortDescription';
import CardDetails from './MuiCardBaseShortDetails';
import CardHeaderWithTitle from './MuiCardBaseShortHeaderWithTitle';
import CardMainContent from './MuiCardBaseShortMainContent';
import CardMetadata from './MuiCardBaseShortMetadata';
import ShowLessButton from './MuiCardBaseShortShowLessButton';

export const MuiCardBaseShort: React.FC<MuiCardBaseProps> = (props) => {
	const {
		id,
		index,
		contentType,
		publisher,
		cover,
		isReadOnly,
		title,
		description,
		subjects,
		classYears,
		schoolTypes,
		link,
		linkFunction,
		date,
		chipText,
		isPinned,
		isNew,
		itemData,
		isFirstElement,
		aiMainTopic,
		aiProductItemSubject,
		aiProductItemClassYears,
		aiProductItemSchoolType,
		mindItemPageNumber,
		secondaryLink,
	} = props;

	const { t } = useTranslation();
	const { router, match } = useRouter();
	const [isTruncated, setIsTruncated] = useState(true);
	const toggleTruncated = () => setIsTruncated(!isTruncated);

	const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
		e.stopPropagation();
		const position = index !== undefined ? index + 1 : undefined; // Convert 0-indexed to 1-indexed
		const linkTo = getCardLinkProps(to, match.location.pathname, position);
		linkFunction ? linkFunction() : router.push(linkTo);
	};

	const itemLink = getItemLink({ id, contentType, mindItemPageNumber });

	return (
		<CardContainer contentType={contentType} isNew={isNew} isTruncated={isTruncated}>
			<AddMenuButton
				isPinned={isPinned}
				type={contentType}
				id={id}
				itemData={itemData}
				index={index}
				isFirstElement={isFirstElement}
			/>
			<CardMainContent>
				<CardCover
					contentType={contentType}
					cover={cover}
					itemLink={itemLink}
					isWide={contentType === 'video'}
					withPlayIcon={contentType === 'video'}
					title={title}
					index={index}
				/>
				<CardContentWrapper isTruncated={isTruncated}>
					<CardHeaderWithTitle
						header={t(mapContentTypeName(contentType))}
						title={title}
						caption={publisher}
						itemLink={itemLink}
						isTruncated={isTruncated}
						index={index}
					/>
					<CardMetadata toggleTruncated={toggleTruncated} isTruncated={isTruncated}>
						{chipText && <span>{chipText}</span>}
						{aiProductItemClassYears && aiProductItemClassYears.length > 0 ? (
							<>
								<span>•</span>
								<span>
									{`${compressClassYearArray(aiProductItemClassYears.map(Number)).join(', ')} ${t('Class year')} `}
								</span>
							</>
						) : (
							classYears &&
							classYears.length > 0 && (
								<>
									<span>•</span>
									<span>{`${compressClassYearArray(classYears).join(', ')} ${t('Class year')} `}</span>
								</>
							)
						)}
					</CardMetadata>
				</CardContentWrapper>
			</CardMainContent>

			{!isTruncated && (
				<>
					<ShowLessButton toggleTruncated={toggleTruncated} />
					{isReadOnly && (
						<Box>
							<Chip label={t('Read only')} size="small" />
						</Box>
					)}
					{aiMainTopic && <AiMainTopic topic={aiMainTopic} />}
					<CardDescription description={description} truncateDescriptionAt={2} />
					<CardDetails>
						{link && (
							<MuLink
								underline="none"
								onClick={(e) => handleLinkClick(e, link.to)}
								data-testid="mui-card-link"
							>
								<Metadata
									icon={link.icon}
									label={link.label}
									sx={{ color: undefined, stroke: undefined }}
								/>
							</MuLink>
						)}
						{aiProductItemSubject ? (
							<Metadata icon="tag" label={aiProductItemSubject.join(', ')} />
						) : (
							subjects && <Metadata icon="tag" label={subjects.join(', ')} />
						)}
						{aiProductItemSchoolType && aiProductItemSchoolType.length > 0 ? (
							<Metadata icon="schoolType" label={aiProductItemSchoolType.join(', ')} />
						) : (
							schoolTypes &&
							schoolTypes.length > 0 && <Metadata icon="schoolType" label={schoolTypes.join(', ')} />
						)}
						{date && <Metadata icon="calendar" label={date} />}
						{secondaryLink && (
							<MuLink
								underline="none"
								onClick={(e) => {
									e.stopPropagation();
									const position = index !== undefined ? index + 1 : undefined;
									router.push(getCardLinkProps(secondaryLink.to, match.location.pathname, position));
								}}
								data-testid="mui-card-secondary-link"
							>
								<Metadata
									icon={secondaryLink.icon}
									label={secondaryLink.label}
									sx={{ color: undefined, stroke: undefined }}
								/>
							</MuLink>
						)}
					</CardDetails>
				</>
			)}
		</CardContainer>
	);
};

export default MuiCardBaseShort;
